import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleDots2Icon, PlusIcon, TrashIcon } from '@radix-ui/react-icons';

import { Button } from '../../../primitives/button';
import { InputNumber } from '../../../primitives/input-number';
import { SelectModel } from '../../select/select-model';
import { TIME_UNIT_OPTIONS } from '../constants';
import { ScheduleIncrement } from '../types';

interface IncrementsEditorProps {
  disabled?: boolean;
  id?: string;
  increments: ScheduleIncrement[];
  onChange: (increments: ScheduleIncrement[]) => void;
}

interface SortableIncrementProps {
  disabled: boolean;
  id?: string;
  increment: ScheduleIncrement;
  index: number;
  onRemove: (index: number) => void;
  onUpdate: (index: number, patch: Partial<ScheduleIncrement>) => void;
  selectedUnits: Set<ScheduleIncrement['unit']>;
}

function SortableIncrement({
  disabled,
  id,
  increment,
  index,
  onRemove,
  onUpdate,
  selectedUnits,
}: SortableIncrementProps) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: increment.unit,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      className="flex items-center gap-2"
      style={{
        opacity: isDragging ? 0.7 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Button
        aria-label="Arrastrar para reordenar desplazamiento"
        className="shrink-0 cursor-grab touch-none"
        disabled={disabled}
        size="icon-sm"
        title="Arrastrar para reordenar desplazamiento"
        variant="ghost"
        {...attributes}
        {...listeners}
      >
        <DragHandleDots2Icon />
      </Button>
      <InputNumber
        className="w-[5rem]"
        id={id ? `${id}-value-${index}` : undefined}
        disabled={disabled}
        min={1}
        value={increment.value}
        onValueChange={(value) => onUpdate(index, { value: value ?? 0 })}
      />
      <SelectModel
        className="flex-1"
        disabled={disabled}
        id={id ? `${id}-unit-${index}` : undefined}
        model={TIME_UNIT_OPTIONS.filter(
          ({ value }) => value === increment.unit || !selectedUnits.has(value),
        ).map((option) => ({
          value: option.value,
          item: option.pluralLabel,
        }))}
        onValueChange={(unit) => onUpdate(index, { unit: unit as ScheduleIncrement['unit'] })}
        value={increment.unit}
      />
      <Button
        aria-label="Quitar desplazamiento"
        title="Quitar desplazamiento"
        className="shrink-0 opacity-60"
        disabled={disabled}
        onClick={() => onRemove(index)}
        size="icon-sm"
        variant="destructive"
      >
        <TrashIcon />
      </Button>
    </div>
  );
}

/**
 * Ordered list editor for {@link ScheduleIncrement}s. Order matters because increments are applied
 * sequentially, so rows can be reordered vertically with drag and drop.
 */
export function IncrementsEditor({
  disabled = false,
  id,
  increments,
  onChange,
}: IncrementsEditorProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function updateRow(index: number, patch: Partial<ScheduleIncrement>) {
    onChange(
      increments.map((increment, i) => (i === index ? { ...increment, ...patch } : increment)),
    );
  }

  function removeRow(index: number) {
    onChange(increments.filter((_, i) => i !== index));
  }

  function addRow() {
    const unit = availableUnits.includes('days') ? 'days' : availableUnits[0];

    if (unit) {
      onChange([...increments, { value: 1, unit }]);
    }
  }

  const selectedUnits = new Set(increments.map(({ unit }) => unit));
  const availableUnits = TIME_UNIT_OPTIONS.filter(({ value }) => !selectedUnits.has(value)).map(
    ({ value }) => value,
  );

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;

    const oldIndex = increments.findIndex(({ unit }) => unit === active.id);
    const newIndex = increments.findIndex(({ unit }) => unit === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    onChange(arrayMove(increments, oldIndex, newIndex));
  }

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={increments.map(({ unit }) => unit)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {increments.map((increment, index) => (
              <SortableIncrement
                disabled={disabled}
                id={id}
                increment={increment}
                index={index}
                key={increment.unit}
                onRemove={removeRow}
                onUpdate={updateRow}
                selectedUnits={selectedUnits}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button
        aria-label="Agregar desplazamiento"
        title="Agregar desplazamiento"
        disabled={disabled || availableUnits.length === 0}
        onClick={addRow}
        size="icon-sm"
      >
        <PlusIcon />
      </Button>
    </div>
  );
}
